import { Button, Alert } from "@a2z/ui";

import { useEffect, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useStoreSubscriptionPaymentProofUrlQuery } from "@/modules/stores/services/queries/useStoreSubscriptionPaymentProofUrlQuery";

type Props = {
  storeId: string;
  paymentId: string;
};

export function SubscriptionPaymentProofModal({ storeId, paymentId }: Props) {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const {
    data,
    isLoading,
    error: proofError,
    refetch,
  } = useStoreSubscriptionPaymentProofUrlQuery({
    storeId: isOpen ? storeId : "",
    paymentId: isOpen ? paymentId : "",
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const openModal = () => {
    setIsOpen(true);
    void refetch();
  };

  const proofUrl = data?.url ?? null;
  const error =
    proofError instanceof ApiJsonClientError
      ? proofError.message || t(keys.errors.generic)
      : null;

  // Restart the image spinner whenever a new proof URL arrives
  // (adjust-state-during-render pattern instead of an effect).
  const [prevProofUrl, setPrevProofUrl] = useState(proofUrl);
  if (proofUrl !== prevProofUrl) {
    setPrevProofUrl(proofUrl);
    setIsImageLoading(Boolean(proofUrl));
  }

  if (!isOpen) {
    return (
      <Button
        type="button"
        onClick={openModal}
        variant="outline" className="h-8 rounded-full px-3 text-[13px] font-semibold transition max-lg:h-10"
      >
        {t(keys.admin.subscriptionPaymentProofViewAction)}
      </Button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 pt-10 pb-[max(2.5rem,env(safe-area-inset-bottom))]"
      role="dialog"
      aria-modal="true"
      onClick={() => setIsOpen(false)}
    >
      <div
        className="max-h-full w-full max-w-2xl overflow-y-auto rounded-2xl bg-[color:var(--surface)] p-6 shadow-[var(--shadow-md)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-[color:var(--text-primary)]">
              {t(keys.admin.subscriptionPaymentProofTitle)}
            </h2>
            <p className="mt-1 text-xs text-[color:var(--text-muted)]">
              {t(keys.admin.subscriptionPaymentProofHint)}
            </p>
          </div>
          <Button variant="ghost"
            type="button"
            onClick={() => setIsOpen(false)}
            className="text-xs font-semibold text-[color:var(--text-muted)]"
          >
            {t(keys.common.close)}
          </Button>
        </div>

        <div className="mt-5">
          {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
          {isLoading ? (
            <div className="flex items-center gap-2 text-xs text-[color:var(--text-muted)]">
              <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-[color:var(--border-light)] border-t-transparent" />
              {t(keys.common.loading)}
            </div>
          ) : proofUrl ? (
            <div className="grid gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={proofUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-[color:var(--text-muted)]"
                >
                  {t(keys.admin.subscriptionPaymentProofOpenTab)}
                </a>
              </div>
              <div className="relative h-[360px] overflow-hidden rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)]">
                <div
                  className={`absolute inset-0 flex items-center justify-center bg-black/10 transition-opacity duration-500 ${
                    isImageLoading ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <span className="inline-flex h-9 w-9 animate-spin rounded-full border-2 border-[color:var(--primary-500)] border-t-transparent" />
                </div>
                <img
                  src={proofUrl}
                  alt={t(keys.admin.subscriptionPaymentProof)}
                  className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-500 ${
                    isImageLoading ? "opacity-0" : "opacity-100"
                  }`}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => setIsImageLoading(false)}
                />
              </div>
            </div>
          ) : (
            <div className="text-xs text-[color:var(--text-muted)]">
              {t(keys.admin.subscriptionPaymentProofMissing)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
